import { useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

import LiffApp from "./liff/LiffApp";
import ZippyAndMeApp from "./zippyAndMe/ZippyAndMeApp";

import Loader from "./components/ui/Loader";
import useStore from "./hooks/store/useStore";
import GameLayout from "./layout/game/GameLayout";
import HomeLayout from "./layout/home/HomeLayout";
import changeLanguageByMarket from "./locales/utils/changeLanguageByMarket";

import TokenExpired from "./pages/tokenExpired/TokenExpired";
import WebLogin from "./pages/webLogin/WebLogin";
import getWebLoginTokenFromStorage from "./pages/webLogin/utils/getWebLoginTokenFromStorage";
import navigateToLeadPage from "./pages/webLogin/utils/navigateToLeadPage";
import Story from "./pages/story/Story";
import DemoMagicPen from "./pages/demo/magicPen/DemoMagicPen";
import DemoTalkAlongCards from "./pages/demo/talkAlongCards/DemoTalkAlongCards";
import DemoQACards from "./pages/demo/qaCards/DemoQACards";

import useExchangeToken from "./services/useExchangeToken";
import useQueryCustom from "./services/utils/useQueryCustom";
import useOneTimeToken from "./services/useOneTimeToken";

import getFontFamily from "./styles/utils/getFontFamily";
import importFont from "./styles/utils/importFont";
import getTokenId from "./utils/getTokenId";
import isValidMarket from "./utils/isValidMarket";
import useMetaPixel from "./hooks/useMetaPixel";
import { MenuItemType, MarketType, SourceType } from "./types";

export default function App() {
  if (window.location.pathname.startsWith("/line/")) return <LiffApp />;

  if (window.location.pathname.startsWith("/zippy-and-me/magic-link/"))
    return <ZippyAndMeApp />;

  return <DigitalExperienceApp />;
}

function DigitalExperienceApp() {
  const is404 = window.location.pathname === "/404";

  const [showDisneyMenu, setShowDisneyMenu] = useState<boolean | null>(null);
  const [selectedMenuItem, setSelectedMenuItem] =
    useState<MenuItemType>("event");
  const [fontLoaded, setFontLoaded] = useState(false);

  const { source, setSource, language, setLanguage } = useStore(
    (state) => state.global,
  );
  const { accessKey, jwtToken, setTokens } = useStore((state) => state.tokens);
  const { userInformation, setUserInformation } = useStore(
    (state) => state.user,
  );

  const exchangeTokenMutation = useExchangeToken();
  const oneTimeTokenMutation = useOneTimeToken();

  const { data: userInformationData } = useQueryCustom(
    "userInformation",
    "user-information",
    Boolean(accessKey && jwtToken),
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const sourceData =
      (searchParams.get("source") as SourceType) || SourceType.Web;

    if (is404) {
      const market = searchParams.get("market") as MarketType | null;

      changeLanguageByMarket(market || "TW", setLanguage);
      setShowDisneyMenu(false);
    } else {
      switch (sourceData) {
        case SourceType.ClubApp:
          exchangeTokenMutation.mutate(getTokenId());

          break;

        case SourceType.Web: {
          const tokens = getWebLoginTokenFromStorage();

          if (tokens) setTokens(tokens);

          break;
        }

        case SourceType.DemoApp:
          changeLanguageByMarket("KR", setLanguage);
          setShowDisneyMenu(false);

          break;
      }
    }

    setSource(sourceData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userInformationData) setUserInformation(userInformationData);
  }, [userInformationData, setUserInformation]);

  useEffect(() => {
    if (!userInformation) return;

    const {
      market,
      trialUserSegmentation: segmentation,
      deviceId,
      leadsLinkAddress,
    } = userInformation;

    if (
      source === SourceType.Web &&
      deviceId === "WEB" &&
      segmentation === "Awareness" &&
      leadsLinkAddress
    ) {
      const navigateToLeadPageWithToken = async () => {
        const { tokenId } = await oneTimeTokenMutation.mutateAsync();

        navigateToLeadPage(leadsLinkAddress, tokenId);
      };

      navigateToLeadPageWithToken();

      return;
    }

    const shouldShowDisneyMenu =
      (market === "TW" || market === "HK" || market === "JP") &&
      (source === SourceType.Web || segmentation !== "Action");

    changeLanguageByMarket(market, setLanguage);
    setShowDisneyMenu(shouldShowDisneyMenu);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInformation, setLanguage, setShowDisneyMenu]);

  useEffect(() => {
    if (!language) return;

    importFont(language, setFontLoaded);
  }, [language]);

  const marketParam = new URLSearchParams(window.location.search).get("market");

  const pixelMarket: MarketType | null =
    (userInformation?.market as MarketType) ||
    (marketParam as MarketType) ||
    null;

  useMetaPixel(pixelMarket);

  if (
    (source === SourceType.Web || source === SourceType.DemoApp) &&
    !isValidMarket(marketParam, userInformation)
  )
    return <div>Error: This page is not available in your region</div>;

  if (source === SourceType.DemoApp && marketParam !== "KR")
    return <div>This page is not available in your region</div>;

  if (source === SourceType.Web && !Boolean(accessKey && jwtToken))
    return <WebLogin market={marketParam as MarketType} />;

  if (exchangeTokenMutation.isError && !is404)
    return (
      <div>An error occurred, please retry or contact the administrator</div>
    );

  if (!source || !language || showDisneyMenu === null || !fontLoaded)
    return <Loader />;

  const router = createBrowserRouter([
    {
      path: "/",
      element: (
        <HomeLayout
          selectedMenuItem={selectedMenuItem}
          setSelectedMenuItem={setSelectedMenuItem}
          showDisneyMenu={showDisneyMenu}
          setShowDisneyMenu={setShowDisneyMenu}
        />
      ),
      errorElement: <div>Page Not Found</div>,
    },
    {
      path: "404",
      element: (
        <HomeLayout
          selectedMenuItem={selectedMenuItem}
          setSelectedMenuItem={setSelectedMenuItem}
          showDisneyMenu={false}
          setShowDisneyMenu={setShowDisneyMenu}
          contentOverride={<TokenExpired />}
        />
      ),
    },
    {
      path: "game",
      element: <GameLayout />,
    },
    {
      path: "story",
      element: <Story />,
    },
    {
      path: "demo/magicPen",
      element: <DemoMagicPen />,
    },
    {
      path: "demo/talkAlongCards",
      element: <DemoTalkAlongCards />,
    },
    {
      path: "demo/QACards",
      element: <DemoQACards />,
    },
  ]);

  return (
    <div
      style={{ fontFamily: getFontFamily(language) }}
      className="overflow-hidden"
    >
      <RouterProvider router={router} />
    </div>
  );
}
